// Client-side User Profile Repair Service
// Creates missing user profiles directly via Supabase client

import { supabase } from '@/config/supabase'

interface ProfileRepairResult {
  success: boolean
  status: 'exists' | 'created' | 'error'
  message: string
  profile?: any
}

export class UserProfileClientRepair {
  /**
   * Check and create user profile if missing
   */
  async repairUserProfile(): Promise<ProfileRepairResult> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser()

      if (authError || !user) {
        return {
          success: false,
          status: 'error',
          message: 'User not authenticated'
        }
      }

      // Check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle() // Use maybeSingle to avoid error when no rows found

      if (checkError) {
        console.error('Error checking profile:', checkError)
        return {
          success: false,
          status: 'error',
          message: `Database error: ${checkError.message}`
        }
      }

      if (existingProfile) {
        return {
          success: true,
          status: 'exists',
          message: 'Profile already exists',
          profile: existingProfile
        }
      }

      // Profile doesn't exist, create it
      const newProfile = {
        id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.full_name || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        subscription_tier: 'professional', // Default for development
        subscription_status: 'free', // Default status
        role: 'user',
        stripe_customer_id: null,
        credits_balance: 2000, // Give development credits (correct field name)
        credits_used_this_month: 0, // Initialize monthly usage tracking
        trial_credits_used: 0 // Initialize as number, not boolean
      }

      const { data: createdProfile, error: createError } = await supabase
        .from('user_profiles')
        .insert(newProfile)
        .select()
        .single()

      if (createError) {
        console.error('Error creating profile:', createError)
        return {
          success: false,
          status: 'error',
          message: `Failed to create profile: ${createError.message}`
        }
      }

      return {
        success: true,
        status: 'created',
        message: 'Profile created successfully',
        profile: createdProfile
      }

    } catch (error) {
      console.error('Unexpected error in profile repair:', error)
      return {
        success: false,
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Check if user profile exists
   */
  async checkProfileExists(): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle()

      return !error && !!profile
    } catch (error) {
      console.error('Error checking profile existence:', error)
      return false
    }
  }

  /**
   * Get detailed profile info for debugging
   */
  async getProfileDebugInfo() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        return { user: null, profile: null, error: 'No authenticated user' }
      }

      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

      return {
        user: {
          id: user.id,
          email: user.email,
          created_at: user.created_at,
          user_metadata: user.user_metadata
        },
        profile,
        error: error?.message || null
      }
    } catch (error) {
      return {
        user: null,
        profile: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

export const userProfileClientRepair = new UserProfileClientRepair()
export default userProfileClientRepair